import { useMemo, useState } from 'react';
import { line, Color, DEAL_AUTHORITY_ROLES, DEAL_AUTHORITY_ROLE_LABELS, Size, authorityLinks, dealContactTags, t, type AuthorityReach, type DealAuthorityRole, type DrawnTag, type SuggestionRow } from '@bms2/shared';
import { Button, Checkbox, Dropdown, Pill, Select, Textarea } from '../../components/controls';
import { Icon } from '../../components/Icon';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { PersonRowList, type TileItem, type TilePersonItem } from '../../components/composites/Tile';
import { PAGES_PATH, del, post, put, useApi } from './api';
import { useAddNew } from './AddNewHost';
import { TagPill } from './TagSwatch';
import type { TileAnswer } from './tileItems';
import styles from './DealAuthority.module.css';

/** One picked person as the server sends them (decision 0618). */
export interface AuthorityRowView {
  personId: string;
  personName: string;
  role: DealAuthorityRole;
  roleLabel: string;
  note: string | null;
  addedAt: string;
  title: string | null;
  /** False once this person's employment at the buying company has ended; the row stays either way. */
  stillThere: boolean;
  /** 🔒 The person's own avatar colour, so the stacked row draws the circle the Contacts tile draws for them (decision 0719). */
  avatarColor?: string | null;
  /** 🔒 The number and the address the row's Call and Email reach (decision 6001); absent from an older reply. */
  reach?: AuthorityReach | null;
}

/**
 * 🔒 The Authority person's Call and Email (decision 6001; row T2778), as the
 * phone's record header draws them: `tel:` and `mailto:` links, each hidden
 * where the person carries no such line. A link and never a button, so the
 * device's own handler dials or opens a draft.
 */
function AuthorityReachLinks({ row }: { row: AuthorityRowView }) {
  const links = authorityLinks(row.reach);
  if (!links.call && !links.email) return null;
  return (
    <span className={styles.reach} data-deal-authority-reach={row.personId}>
      {links.call ? (
        <a className={`${styles.reachLink} ${styles.reachCall}`} href={links.call} aria-label={line(t('record.call'), row.personName)} data-deal-authority-call={row.personId}>
          <Icon meaning="callNumber" size="inline" />
          {t('record.call')}
        </a>
      ) : null}
      {links.email ? (
        <a className={styles.reachLink} href={links.email} aria-label={line(t('record.email'), row.personName)} data-deal-authority-email={row.personId}>
          <Icon meaning="mail" size="inline" />
          {t('record.email')}
        </a>
      ) : null}
    </span>
  );
}

interface AuthorityAnswer {
  rows: AuthorityRowView[];
  candidates: { personId: string; label: string; title: string | null }[];
  /** The deal's company, whose roster the picker offers; absent from an older reply. */
  company?: { id: string; name: string } | null;
}

/** The Deal's authority list is read and written under the Deal, on the record pages' own root. */
export const dealAuthorityPath = (dealId: string) => `${PAGES_PATH}/deals/${dealId}/authority`;

const roleOptions = DEAL_AUTHORITY_ROLES.map((role) => ({ value: role, label: DEAL_AUTHORITY_ROLE_LABELS[role] }));

/** The part a tagged person plays, as the one pill the Authority row and the contact row both draw. */
function RolePill({ row }: { row: AuthorityRowView }) {
  return <Pill text={row.roleLabel} foreground="var(--status-inactive-text)" background="var(--status-inactive-bg)" size={Size.Small} outlined />;
}

/**
 * 🔒 The Authority tag on one contact row of the Contacts tile (decision 1405):
 * a checkbox beneath the name, and the part they play as a pill beside it once
 * set. Ticking it opens the Authority window with the person fixed, because the
 * part is required; clearing it clears the tag and keeps the contact. A Lost
 * Deal's contact draws the tag without the checkbox.
 */
function DealContactAuthorityTag({ dealId, personId, personName, tag, editable, apiBaseUrl, onChanged, onError }: {
  dealId: string;
  personId: string;
  personName: string;
  tag: AuthorityRowView | null;
  editable: boolean;
  apiBaseUrl: string;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const clear = async () => {
    const answer = await del(`${dealAuthorityPath(dealId)}/${personId}`, apiBaseUrl);
    if (!answer.ok) return onError(answer.message);
    onChanged();
  };
  return (
    <span className={styles.under} data-deal-contact-authority={tag ? 'set' : 'clear'}>
      {editable ? (
        <Checkbox size={Size.Small} label={t('deal.authority.tag')} checked={tag !== null} onChange={(next) => (next ? setOpen(true) : void clear())} />
      ) : (
        <span className={styles.fact}>{t('deal.authority.tag')}</span>
      )}
      {tag ? <span data-deal-contact-authority-role={tag.role}><RolePill row={tag} /></span> : null}
      {open ? (
        <DealAuthorityModal
          open
          dealId={dealId}
          person={{ personId, personName }}
          apiBaseUrl={apiBaseUrl}
          onCancel={() => setOpen(false)}
          onSaved={() => { setOpen(false); onChanged(); }}
        />
      ) : null}
    </span>
  );
}

/**
 * 🔒 The Contacts tile's rows with the Authority tag beneath each open contact
 * (decision 1405). The tag is read from the page answer's `authority`, the same
 * read the Intent card's Authority row draws, so one person is one contact and
 * the two places cannot disagree. Each row is drawn by the tile's own item
 * builder, one at a time, so the tag lands on the row it belongs to.
 */
export function withAuthorityTags(tile: TileAnswer, draw: (tile: TileAnswer) => TileItem[], options: {
  dealId: string;
  authority: readonly AuthorityRowView[];
  editable: boolean;
  apiBaseUrl: string;
  onChanged: () => void;
  onError: (message: string) => void;
  /** 🔒 Each contact's own tags, keyed by person (decision 2262); absent from an older reply. */
  contactTags?: Readonly<Record<string, readonly DrawnTag[]>>;
}): TileItem[] {
  const rows = (tile.rows ?? []).filter((row) => row.label !== '' || row.value);
  if (rows.length === 0) return draw(tile);
  const tagged = new Map(options.authority.map((row) => [row.personId, row]));
  return rows.flatMap((row) => draw({ ...tile, rows: [row] }).map((item): TileItem => {
    const personId = typeof row.raw?.personId === 'string' ? row.raw.personId : null;
    // 🔨 An ended contact carries no tag: ending a contact clears it (decision 1405).
    if (item.type !== 'person' || personId === null || row.raw?.ended === true) return item;
    const tag = tagged.get(personId) ?? null;
    // 🔒 The person's categorized tags draw after the Authority pill, four and a +N (decision 2262).
    const personTags = dealContactTags(options.contactTags?.[personId] ?? []);
    const drawsTags = personTags.shown.length > 0;
    if (tag === null && !options.editable && !drawsTags) return item;
    return {
      ...item,
      data: { ...(item.data ?? {}), 'data-deal-contact': personId },
      below: (
        <span className={styles.under}>
          {tag !== null || options.editable ? <DealContactAuthorityTag dealId={options.dealId} personId={personId} personName={row.label} tag={tag} editable={options.editable} apiBaseUrl={options.apiBaseUrl} onChanged={options.onChanged} onError={options.onError} /> : null}
          {drawsTags ? <DealContactTags personId={personId} shown={personTags.shown} more={personTags.more} /> : null}
        </span>
      ),
    };
  }));
}

/**
 * 🔒 A contact's own categorized tags on the Deal's contact row (decision 2262):
 * the pills the Person's banner draws, small, with their tooltips and notes,
 * and a count of the rest. The row reads them and writes none.
 */
function DealContactTags({ personId, shown, more }: { personId: string; shown: readonly DrawnTag[]; more: number }) {
  return (
    <span className={styles.contactTags} data-deal-contact-tags={personId}>
      {shown.map((tag) => <TagPill key={tag.id} tag={tag} size={Size.Small} />)}
      {more > 0 ? <span data-deal-contact-tags-more={more}><Pill text={`+${more}`} foreground="var(--status-inactive-text)" background="var(--status-inactive-bg)" size={Size.Small} outlined /></span> : null}
    </span>
  );
}

/**
 * 🔒 The people who decide, drawn beneath the Intent card's Authority row
 * (decision 0618). 🔒 Each is the stacked person row the Contacts tile draws
 * (decision 0719, amending 0618's layout): the avatar, the name with the pencil
 * right after it, the part they play as a pill with their title under the name,
 * their own note beneath that, and Clear tag at the far right. The row's own note
 * box above stays the summary of the whole committee.
 *
 * 🔨 A person who has since left the customer keeps their row and is marked as
 * gone rather than dropped, because the Deal records who decided at the time.
 */
export function DealAuthorityList({ dealId, rows, editable, apiBaseUrl = '', onChanged, onError }: {
  dealId: string;
  rows: readonly AuthorityRowView[];
  editable: boolean;
  apiBaseUrl?: string;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  /** Which row the one window is open on: a person's id to edit theirs, `new` to add. */
  const [editing, setEditing] = useState<string | null>(null);
  const remove = async (personId: string) => {
    const answer = await del(`${dealAuthorityPath(dealId)}/${personId}`, apiBaseUrl);
    if (!answer.ok) return onError(answer.message);
    onChanged();
  };
  return (
    <div className={styles.block} data-deal-authority="block">
      <p className={styles.blockLabel}>{line(t('deal.authority.label'), `(${rows.length})`)}</p>
      {rows.length === 0 ? (
        <p className={styles.empty} data-deal-authority-empty="true">{t('deal.authority.empty')}</p>
      ) : (
        <PersonRowList
          items={rows.map((row): TilePersonItem => ({
            type: 'person',
            id: row.personId,
            person: { displayName: row.personName, avatarColor: row.avatarColor ?? 'avatar-1' },
            attending: 'NeedsAction',
            route: `/people/${row.personId}`,
            data: { 'data-deal-authority-row': row.personId },
            ...(editable
              ? {
                  onEdit: () => setEditing(row.personId),
                  editLabel: line(t('deal.authority.edit'), row.personName),
                  // 🔨 Clearing the tag keeps the contact (decision 1405), so the control says what it does.
                  button: { label: t('deal.authority.clear'), onAct: () => void remove(row.personId) },
                }
              : {}),
            below: (
              <>
                <span className={styles.under}>
                  <span data-deal-authority-role={row.role}>
                    <RolePill row={row} />
                  </span>
                  {row.stillThere ? (
                    row.title ? <span className={styles.fact}>{row.title}</span> : null
                  ) : (
                    <span className={styles.fact} data-deal-authority-left={row.personId}>{t('deal.authority.left')}</span>
                  )}
                </span>
                {row.note ? <span className={styles.note} data-deal-authority-note={row.personId}>{row.note}</span> : null}
                <AuthorityReachLinks row={row} />
              </>
            ),
          }))}
        />
      )}
      {editable ? (
        <div className={styles.control} data-deal-authority-action="add">
          <Button size={Size.Small} color={Color.Navy} text={[`+ ${t('deal.authority.add')}`]} actions={[() => setEditing('new')]} />
        </div>
      ) : null}
      {/* 🔨 Keyed to the row it edits: a modal mounted and hidden reads its initial prop once, so a second row would open on the first one's draft. */}
      <DealAuthorityModal
        key={editing ? `deal-authority-${editing}` : 'deal-authority-closed'}
        open={editing !== null}
        dealId={dealId}
        initial={editing && editing !== 'new' ? rows.find((row) => row.personId === editing) ?? null : null}
        apiBaseUrl={apiBaseUrl}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); onChanged(); }}
      />
    </div>
  );
}

/**
 * 🔒 One window over the deal, keyed to the person it edits (decision 0618): a
 * picker of the deal company's current people, the part they play and a note.
 *
 * 🔨 The picker offers that company's roster and nobody else's, and a person
 * already on the list is not offered again — the server's own read decides
 * both, so the browser cannot widen it. On an edit the person is fixed and
 * their name reads in place of the picker.
 */
export function DealAuthorityModal({ open, dealId, initial = null, person = null, apiBaseUrl = '', onCancel, onSaved }: {
  open: boolean;
  dealId: string;
  initial?: AuthorityRowView | null;
  /** 🔒 A contact tagged from their own row (decision 1405): the person is fixed and the window asks the part and the note. */
  person?: { personId: string; personName: string } | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const fixed = initial ?? person;
  const answer = useApi<AuthorityAnswer>(open && !fixed ? dealAuthorityPath(dealId) : null, apiBaseUrl);
  const [personId, setPersonId] = useState<string | null>(fixed?.personId ?? null);
  const [personName, setPersonName] = useState<string | null>(fixed?.personName ?? null);
  const [role, setRole] = useState<string>(initial?.role ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  /**
   * 🔒 Add New Person (T311; decision 0874): the person and employment window
   * locked to the deal's company, so the new person is on the roster the server
   * offers, and the saved person lands in the picker.
   */
  const addNew = useAddNew(apiBaseUrl);
  const company = answer.state.kind === 'ready' ? answer.state.data.company ?? null : null;
  /** 🔨 Held steady between renders: a search function with a new identity every render restarts the picker's own fetch. */
  const candidates = useMemo(() => (answer.state.kind === 'ready' ? answer.state.data.candidates : []), [answer.state]);
  /**
   * 🔨 The offer is searched where it stands, not re-fetched per keystroke: the
   * roster arrived whole with the read and a customer's roster is a list
   * somebody calls down, not a corpus.
   */
  const search = useMemo(
    () => async (query: string): Promise<SuggestionRow[]> => {
      const term = query.trim().toLowerCase();
      return candidates
        .filter((row) => term === '' || row.label.toLowerCase().includes(term))
        .slice(0, 20)
        .map((row) => ({ id: row.personId, line1: row.label, ...(row.title ? { line2: row.title } : {}) }));
    },
    [candidates],
  );
  const save = async () => {
    setError(null);
    if (!initial && !personId) return setError(t('deal.authority.needsPerson'));
    if (!role) return setError(t('deal.authority.needsRole'));
    const body = { role, note: note.trim() || null };
    const outcome = initial
      ? await put(`${dealAuthorityPath(dealId)}/${initial.personId}`, body, apiBaseUrl)
      : await post(dealAuthorityPath(dealId), { ...body, personId }, apiBaseUrl);
    if (!outcome.ok) return setError(outcome.message);
    onSaved();
  };
  return (
    <Modal
      open={open}
      title={initial ? t('deal.authority.editTitle') : t('deal.authority.title')}
      onCancel={onCancel}
      dataModal="deal-authority"
      actions={[{ kind: 'primary', label: t('modal.save'), onAct: () => void save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <div data-deal-authority-field="person">
        {fixed ? (
          <p className={styles.fixedPerson} data-deal-authority-person={fixed.personId}>{personName}</p>
        ) : (
          <Dropdown
            chars={32}
            label={t('deal.authority.person')}
            entityLabel={t('deal.authority.entity')}
            search={search}
            minChars={0}
            debounceMs={0}
            value={personName}
            onSelect={(row) => { setPersonId(row.id); setPersonName(row.line1); }}
            onAddNew={() => addNew.open({ kind: 'contact', companyId: company?.id ?? null, companyName: company?.name ?? null, pick: 'person' }, (row) => { setPersonId(row.id); setPersonName(row.line1); })}
          />
        )}
        {!fixed && answer.state.kind === 'ready' && candidates.length === 0 ? (
          <p className={styles.hint} data-deal-authority-no-candidates="true">{t('deal.authority.noCandidates')}</p>
        ) : null}
      </div>
      <Select label={t('deal.authority.role')} value={role} onChange={setRole} options={roleOptions} placeholder={t('edit.none')} dataAttribute={{ 'data-deal-authority-field': 'role' }} chars={24} />
      <Textarea label={t('deal.authority.note')} value={note} onChange={setNote} name="authorityNote" rows={3} size={Size.Large} />
      <p className={styles.hint}>{t('deal.authority.noteHint')}</p>
      {addNew.host}
    </Modal>
  );
}
